import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { ContentStep } from '@cfa/pathway-fe-shared/xp';

import { getStepResource } from '@/utils/contentStep';
import { itemByLanguage } from '@/utils/language';

import PreviewResourceModal from './components/PreviewResourceModal';
import { isPlanResourceReference } from './utils';

interface ResourceNameProps {
  step: ContentStep;
  name: string;
  isPreviewOpen: boolean;
  onPreviewOpenChange: (open: boolean) => void;
}

const ResourceName: React.FC<ResourceNameProps> = ({
  step,
  name,
  isPreviewOpen,
  onPreviewOpenChange,
}) => {
  const resource = getStepResource(step);
  const referencesMap = resource?.references
    ? Object.fromEntries(
        resource.references.map(ref => [ref.language, ref] as const),
      )
    : undefined;

  const resourceReference = itemByLanguage(referencesMap);

  useEffect(() => {
    if (
      isPreviewOpen &&
      (!resourceReference || !isPlanResourceReference(resourceReference))
    ) {
      datadogRum.addError(
        new Error('Resource reference is not a plan resource reference', {
          cause: resourceReference,
        }),
      );
    }
  }, [isPreviewOpen, resourceReference]);

  return (
    <>
      <span
        className="text-variant-body1! w-full cursor-pointer text-left wrap-break-word break-all hover:underline"
        onClick={e => {
          e.stopPropagation();
          onPreviewOpenChange(true);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPreviewOpenChange(true);
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="ResourceName"
      >
        {name.replace(/_/g, ' ')}
      </span>
      {resourceReference && (
        <PreviewResourceModal
          resourceReference={resourceReference}
          resourceId={resource?.id}
          resourceType={resource?.type}
          open={isPreviewOpen}
          onOpenChange={onPreviewOpenChange}
        />
      )}
    </>
  );
};

export default ResourceName;
